import type {
  AssignmentResult,
  EvaluateRulesRequest,
  GenerateZplRequest,
  GenerateZplResponse,
  LintRequest,
  LintResponse,
  OperationContract,
  ResourceContract,
  Result,
} from '@tsagko/label-contracts'
import { err, ok } from '@tsagko/label-contracts'
import { getPrinterProfile } from '@tsagko/label-printer-profiles'
import {
  evaluateRules as pipelineEvaluateRules,
  generateZpl as pipelineGenerateZpl,
  lintTemplate as pipelineLintTemplate,
} from '@tsagko/label-zpl-pipeline'
import { resolveImagesForTemplate } from '../demo/resolve-images'

/**
 * Shared local-operation logic for `generateZpl` / `lintTemplate` /
 * `evaluateRules` — the three `OperationContract` members that run
 * entirely client-side regardless of which adapter (demo or embedded) is
 * mounted. Extracted out of the Demo adapter (originally
 * `apps/designer/src/adapters/demo/operations.ts`) so both adapters share
 * one implementation instead of forking it; only `render` / `resolveImage`
 * / `sampleData` / `getSessionContext` differ per adapter (they are
 * network/transport bound and stay in each adapter's own `operations.ts`).
 *
 * `rc` supplies image resolution for `generateZpl` — host-asset images
 * route through `rc.loadImageAsset`. The Demo adapter passes its
 * IndexedDB-backed `ResourceContract`; the embedded adapter passes its
 * transport-backed one. Both satisfy the same `ResourceContract`
 * interface, so this function does not know or care which adapter it is
 * running under.
 *
 * Deliberate limitations carried over unchanged from the pre-extraction
 * Demo adapter (see git history on `demo/operations.ts` before this
 * task for the original notes):
 *   - Printer profiles are looked up against the built-in catalogue from
 *     `@tsagko/label-printer-profiles` only; a user-saved profile via
 *     `savePrinterProfile` is NOT consulted here.
 *   - When `request.resolvedImages` is omitted, `resolveImagesForTemplate`
 *     resolves `host-asset` / `data-uri` / `url` image sources but not
 *     `field` sources (those require runtime data binding and are skipped
 *     silently, same as before).
 */
export function createLocalOperations(
  rc: ResourceContract
): Pick<OperationContract, 'generateZpl' | 'lintTemplate' | 'evaluateRules'> {
  return {
    async generateZpl(request: GenerateZplRequest): Promise<Result<GenerateZplResponse>> {
      const profile = getPrinterProfile(request.printerProfileId)
      if (!profile) {
        return err(
          'PROFILE_NOT_FOUND',
          `Printer profile ${request.printerProfileId} not found`
        )
      }
      // If the caller already has a pre-rasterized map (e.g. from the canvas
      // preview hook), use it directly. Otherwise resolve+rasterize now.
      const resolvedImages = request.resolvedImages
        ?? await resolveImagesForTemplate(request.template, profile, rc)
      const response = pipelineGenerateZpl(
        request.template,
        request.data,
        profile,
        resolvedImages,
        {},
        request.opts ?? {}
      )
      return ok(response)
    },
    async lintTemplate(request: LintRequest): Promise<Result<LintResponse>> {
      // The pipeline's `lintTemplate` accepts an optional profile. When the
      // request carries a profileId, look it up; missing IDs are silently
      // ignored (lint still runs against template geometry alone). This is
      // intentional: `LintRequest.printerProfileId` is documented as
      // optional and a missing/unknown profile should NOT cause a lint
      // failure — the operator may not have picked a printer yet.
      const profile =
        request.printerProfileId !== undefined
          ? getPrinterProfile(request.printerProfileId)
          : undefined
      const warnings = pipelineLintTemplate(request.template, profile)
      return ok({ warnings })
    },
    async evaluateRules(request: EvaluateRulesRequest): Promise<Result<AssignmentResult>> {
      const result = pipelineEvaluateRules(request.rules, request.data)
      return ok(result)
    },
  }
}
